import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import type { SectionFormValues } from "./types";
import "./style.css";

export interface SectionModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  initialValues: SectionFormValues;
  onClose: () => void;
  onSave: (values: SectionFormValues) => Promise<unknown>;
}

export const SectionModal = ({
  isOpen,
  mode,
  initialValues,
  onClose,
  onSave,
}: SectionModalProps): JSX.Element => {
  const [values, setValues] = useState<SectionFormValues>(initialValues);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setValues(initialValues);
  }, [isOpen, initialValues]);

  const set = <K extends keyof SectionFormValues>(
    k: K,
    v: SectionFormValues[K]
  ) => setValues((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (values.name.trim() === "" || saving) return;
    setSaving(true);
    try {
      await onSave(values);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} dialogClassName="section-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "create" ? "Add Section" : "Edit Section"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label htmlFor="section-name">Name</Form.Label>
          <TextField
            label={null}
            id="section-name"
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
            required
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label htmlFor="section-expression">
            Display Condition (Optional)
          </Form.Label>
          <TextField
            label={null}
            id="section-expression"
            value={values.expression}
            onChange={(e) => set("expression", e.target.value)}
          />
          <small className="text-muted d-block">
            When this evaluates to true the section is shown. Leave blank to
            always show.
          </small>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label htmlFor="section-introduction">Introduction</Form.Label>
          <Form.Control
            as="textarea"
            id="section-introduction"
            rows={3}
            value={values.introduction}
            onChange={(e) => set("introduction", e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || values.name.trim() === ""}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SectionModal;
